<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AnimatedLineIcon from 'dashboard/components-next/icon/AnimatedLineIcon.vue';
import { isConversationRouteName } from 'dashboard/helper/conversationRoutes';

// SiteUp: barra de navegação inferior do PWA mobile. Aparece só em telas
// pequenas (< md) e some dentro de uma conversa aberta (pra dar tela cheia
// ao chat + não conflitar com o campo de resposta). Leva aos destinos mais
// usados no atendimento + um botão "Menu" que abre a sidebar completa (com
// o resto: Team chat, Flow, Tarefas, Configurações, Perfil).
defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggleMenu']);

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isKanbanEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, 'kanban_board')
);

// A barra some quando estamos dentro de uma conversa aberta (tela cheia pro
// chat). O mesmo critério controla o padding do layout no Dashboard.vue, pra
// não sobrar espaço morto embaixo do campo de resposta.
const isConversationRoute = computed(() => isConversationRouteName(route.name));

const matches = (name, patterns) =>
  patterns.some(p => (name || '').startsWith(p) || (name || '').includes(p));

const navItems = computed(() => {
  const items = [
    {
      id: 'conversations',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-ri-chat-1-line',
      activeIcon: 'i-ri-chat-1-fill',
      to: accountScopedRoute('home'),
      match: name =>
        name === 'home' ||
        matches(name, ['conversation', 'conversations_', 'inbox_conversation']),
    },
    {
      id: 'contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-ri-contacts-line',
      activeIcon: 'i-ri-contacts-fill',
      to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
      match: name => matches(name, ['contacts', 'companies']),
    },
  ];

  if (isKanbanEnabled.value) {
    items.push({
      id: 'kanban',
      label: store.getters['kanban/getKanbanTitle'] || 'Kanban',
      icon: 'i-ri-layout-grid-line',
      activeIcon: 'i-ri-layout-grid-fill',
      to: accountScopedRoute('kanban_board'),
      match: name => matches(name, ['kanban']),
    });
  }

  // SiteUp: "Relatórios" saiu da barra inferior mobile — telas de relatório
  // são densas em tabelas/gráficos e não fazem sentido em telas pequenas.
  // A rota continua acessível (admin desktop / deep links); só o atalho some.

  return items;
});

const isActive = item => item.match(route.name);

// Estilo X: o destino em que o usuário está usa o ícone preenchido. As duas
// classes ficam escritas por extenso (acima) porque o Tailwind só gera CSS
// para classe literal — montar `-fill` em runtime some na tela.
const iconFor = item => (isActive(item) ? item.activeIcon : item.icon);
</script>

<template>
  <nav
    v-if="!isConversationRoute"
    id="mobile-bottom-nav"
    class="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t md:hidden border-n-weak bg-n-solid-1/85 backdrop-blur-xl"
    :style="{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }"
  >
    <router-link
      v-for="item in navItems"
      :key="item.id"
      :to="item.to"
      class="flex flex-col items-center justify-center flex-1 gap-1 pt-2 pb-1.5 transition-transform duration-150 outline-none group active:scale-95"
    >
      <span
        class="flex items-center justify-center h-7 transition-colors duration-200 rounded-full px-5"
        :class="
          isActive(item)
            ? 'bg-n-brand/15 text-n-brand'
            : 'text-n-slate-11 group-hover:text-n-slate-12'
        "
      >
        <Icon :icon="iconFor(item)" class="w-[22px] h-[22px]" />
      </span>
      <span
        class="w-full px-0.5 text-center text-[10px] leading-none font-medium transition-colors duration-200 truncate"
        :class="isActive(item) ? 'text-n-brand' : 'text-n-slate-11'"
      >
        {{ item.label }}
      </span>
    </router-link>

    <button
      type="button"
      class="flex flex-col items-center justify-center flex-1 gap-1 pt-2 pb-1.5 transition-transform duration-150 outline-none group active:scale-95"
      @click="emit('toggleMenu')"
    >
      <span
        class="flex items-center justify-center h-7 px-5 transition-colors duration-200 rounded-full"
        :class="
          isMobileSidebarOpen
            ? 'bg-n-brand/15 text-n-brand'
            : 'text-n-slate-11 group-hover:text-n-slate-12'
        "
      >
        <AnimatedLineIcon
          :name="isMobileSidebarOpen ? 'menu-to-close' : 'close-to-menu'"
          class="w-[22px] h-[22px]"
        />
      </span>
      <span
        class="text-[10px] leading-none font-medium transition-colors duration-200"
        :class="isMobileSidebarOpen ? 'text-n-brand' : 'text-n-slate-11'"
      >
        Menu
      </span>
    </button>
  </nav>
</template>
